import { useState, useMemo, useCallback } from "react"
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"
import {
  useGenerateAdsetsMutation,
  useGenerateCreativeAdsetsMutation,
  useLazyGenerateStatusQuery,
  useLaunchCampaignMutation,
  useLazyGetPixelsQuery,
  type GenerateAdsetsResponse,
  type LaunchCampaignResponse,
  type PixelsResponse,
} from "~/ui-rtk/api/campaignBlasterApi"
import MetaAdAccountGate from "~/ui-rtk/components/features/meta-ad-account-gate/MetaAdAccountGate"
import CampaignSetup from "./components/CampaignSetup"
import AdsetReview from "./components/AdsetReview"
import FinalReview from "./components/FinalReview"
import StructureSelection from "./components/StructureSelection"
import CustomStructureBuilder from "./components/CustomStructureBuilder"
import CustomCreativeBuilder from "./components/CustomCreativeBuilder"
import CreativeSelectionStep from "./components/CreativeSelectionStep"
import CreativeDetailsStep from "./components/CreativeDetailsStep"
import {
  DEFAULT_CAMPAIGN_DATA,
  type WorkflowStep,
  type CampaignData,
  type CreativeOverrides,
  type Adset,
  type AdsetStructure,
  type SelectedCreative,
} from "./types"

const STEPS: { key: WorkflowStep; label: string }[] = [
  { key: "setup", label: "Campaign Setup" },
  { key: "structures", label: "Structure" },
  { key: "adsets", label: "Adset Review" },
  { key: "review", label: "Final Review" },
]

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  campaignType: "Campaign Type",
  optimizationGoal: "Optimization Goal",
  attributionModel: "Attribution Model",
  attributionWindow: "Attribution Window",
  biddingStrategy: "Bidding Strategy",
  budgetOptimization: "Budget Optimization",
  budgetAmount: "Budget Amount",
  targetingGeography: "Geography",
  targetingGender: "Gender",
  ageMin: "Age Range",
  ageMax: "Age Range",
  placements: "Placements",
}

function generateCampaignTitle(data: CampaignData): string {
  const parts: string[] = []
  if (data.campaignType) parts.push(data.campaignType.replace(/\s+/g, ""))
  if (data.optimizationGoal) parts.push(`Opt:${data.optimizationGoal}`)
  if (data.attributionModel) {
    parts.push(`Attr:${data.attributionModel.replace(/\s+/g, "")}`)
  }
  if (data.attributionWindow) {
    parts.push(`Win:${data.attributionWindow.replace(/\s+/g, "")}`)
  }
  if (data.targetingGeography) {
    parts.push(`Geo:${data.targetingGeography.replace(/\s+/g, "")}`)
  }
  if (data.targetingGender && data.targetingGender !== "All") {
    parts.push(`Gen:${data.targetingGender}`)
  }
  parts.push(`Age:${data.ageMin}-${data.ageMax}`)
  if (data.placements) parts.push(`Plc:${data.placements.replace(/\s+/g, "")}`)
  return parts.join("_")
}

function createDefaultAdset(data: CampaignData): Adset {
  return {
    id: crypto.randomUUID(),
    title: `${data.adsetStrategy || "Adset"} - 1`,
  }
}

export default function CampaignBlasterPage() {
  const [step, setStep] = useState<WorkflowStep>("setup")
  const [errorFieldHints, setErrorFieldHints] = useState<string[]>([])
  const [campaignData, setCampaignData] = useState<CampaignData>(
    DEFAULT_CAMPAIGN_DATA,
  )
  const [adsets, setAdsets] = useState<Adset[]>([])
  const [structures, setStructures] = useState<AdsetStructure[]>([])
  const [selectedCreatives, setSelectedCreatives] = useState<
    SelectedCreative[]
  >([])
  const [creativeOverrides, setCreativeOverrides] = useState<CreativeOverrides>(
    {},
  )
  const [errorMessage, setErrorMessage] = useState("")
  const [createdCampaignId, setCreatedCampaignId] = useState("")
  const [launchResult, setLaunchResult] =
    useState<LaunchCampaignResponse | null>(null)

  const [selectedPixelId, setSelectedPixelId] = useState<string | null>(null)
  const [pixelsData, setPixelsData] = useState<PixelsResponse | null>(null)
  const [pixelsLoading, setPixelsLoading] = useState(false)
  const [selectedAdAccountId, setSelectedAdAccountId] = useState<string | null>(
    null,
  )

  const [generateAdsets] = useGenerateAdsetsMutation()
  const [generateCreativeAdsets] = useGenerateCreativeAdsetsMutation()
  const [fetchGenerateStatus] = useLazyGenerateStatusQuery()
  const [launchCampaign] = useLaunchCampaignMutation()
  const [fetchPixels] = useLazyGetPixelsQuery()

  const isCreativeFlow = campaignData.adsetStrategy === "Split by Creative"
  const needsPixel =
    campaignData.optimizationGoal === "Max Conversions" ||
    campaignData.optimizationGoal === "Max Conversion Value"

  const pollForCompletion = useCallback(
    async (operationId: string): Promise<GenerateAdsetsResponse> => {
      const POLL_INTERVAL = 5_000
      const MAX_ATTEMPTS = 36 // 3 minutes
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL))
        const { data } = await fetchGenerateStatus(operationId, false)
        if (!data) continue
        if (data.status === "completed" && data.result) {
          return data.result
        }
        if (data.status === "failed") {
          throw new Error(data.error || "Generation failed")
        }
      }
      throw new Error("Generation timed out. Please try again.")
    },
    [fetchGenerateStatus],
  )

  const campaignTitle = useMemo(
    () => generateCampaignTitle(campaignData),
    [campaignData],
  )

  const handleGenerateAdsets = useCallback(async () => {
    setStep("generating")
    setErrorMessage("")
    try {
      const { operationId } = await generateAdsets({
        campaignType: campaignData.campaignType,
        optimizationGoal: campaignData.optimizationGoal,
        targetingGeography: campaignData.targetingGeography,
        targetingGender: campaignData.targetingGender,
        placements: campaignData.placements,
      }).unwrap()

      const result = await pollForCompletion(operationId)

      setStructures(
        result.structures.map(s => ({
          name: s.name,
          description: s.description,
          adsets: s.adsets.map(a => ({
            title: a.title,
            description: a.description,
            exampleHandles: a.exampleHandles ?? [],
          })),
        })),
      )
      setStep("structures")
    } catch {
      setErrorMessage("Failed to generate adsets. Please try again.")
      setStep("error")
    }
  }, [campaignData, generateAdsets, pollForCompletion])

  const handleGenerateCreativeAdsets = useCallback(async () => {
    setStep("generating")
    setErrorMessage("")
    try {
      const { operationId } = await generateCreativeAdsets({
        creatives: selectedCreatives.map(c => ({
          id: c.id,
          type: c.type,
          title: c.title,
          mediaChannel: c.mediaChannel,
          thumbnail: c.thumbnail,
          metrics: c.metrics,
        })),
        campaignType: campaignData.campaignType,
        optimizationGoal: campaignData.optimizationGoal,
      }).unwrap()

      const result = await pollForCompletion(operationId)

      setStructures(
        result.structures.map(s => ({
          name: s.name,
          description: s.description,
          adsets: s.adsets.map(a => ({
            title: a.title,
            description: a.description,
            exampleHandles: a.exampleHandles ?? [],
            creativeIds: a.creativeIds ?? [],
          })),
        })),
      )
      setStep("structures")
    } catch {
      setErrorMessage("Failed to generate creative adsets. Please try again.")
      setStep("error")
    }
  }, [
    campaignData,
    generateCreativeAdsets,
    selectedCreatives,
    pollForCompletion,
  ])

  const handleSetupNext = useCallback(() => {
    if (campaignData.adsetStrategy === "Product Groups") {
      void handleGenerateAdsets()
    } else if (campaignData.adsetStrategy === "Split by Creative") {
      setStep("creativeSelection")
    } else {
      if (adsets.length === 0) {
        setAdsets([createDefaultAdset(campaignData)])
      }
      setStep("adsets")
    }
  }, [adsets.length, campaignData, handleGenerateAdsets])

  const handleStructureSelect = useCallback((structure: AdsetStructure) => {
    const selectedAdsets: Adset[] = structure.adsets.map(a => ({
      id: crypto.randomUUID(),
      title: a.title,
      description: a.description,
      exampleHandles: a.exampleHandles,
      creativeIds: a.creativeIds,
    }))
    setAdsets(selectedAdsets)
    setStep("adsets")
  }, [])

  const handleLaunch = useCallback(async () => {
    setStep("processing")
    setErrorMessage("")
    try {
      const result = await launchCampaign({
        campaignTitle,
        campaignType: campaignData.campaignType,
        optimizationGoal: campaignData.optimizationGoal,
        attributionModel: campaignData.attributionModel,
        attributionWindow: campaignData.attributionWindow,
        adsetStrategy: campaignData.adsetStrategy,
        targetingGeography: campaignData.targetingGeography,
        targetingGender: campaignData.targetingGender,
        placements: campaignData.placements,
        platforms: campaignData.platforms,
        budgetOptimization: campaignData.budgetOptimization,
        biddingStrategy: campaignData.biddingStrategy,
        budgetAmount: campaignData.budgetAmount,
        ageMin: campaignData.ageMin,
        ageMax: campaignData.ageMax,
        ...(selectedPixelId ? { pixelId: selectedPixelId } : {}),
        adsets: adsets.map(a => ({
          title: a.title,
          description: a.description,
          creativeIds: a.creativeIds,
        })),
        ...(isCreativeFlow && selectedCreatives.length > 0
          ? {
              selectedCreatives: selectedCreatives.map(c => ({
                id: c.id,
                type: c.type,
                title: c.title,
                mediaChannel: c.mediaChannel,
              })),
              ...(selectedCreatives.some(c => c.type === "organic") &&
              creativeOverrides.destinationUrl
                ? { creativeOverrides }
                : {}),
            }
          : {}),
      }).unwrap()

      setCreatedCampaignId(result.campaignId)
      setLaunchResult(result)
      setStep("success")
    } catch (err: unknown) {
      const data = (
        err as { data?: { message?: string; fieldHints?: string[] } }
      )?.data
      const message =
        data?.message || "Failed to launch campaign. Please try again."
      setErrorMessage(message)
      setErrorFieldHints(data?.fieldHints ?? [])
      setStep("error")
    }
  }, [
    launchCampaign,
    campaignTitle,
    campaignData,
    adsets,
    isCreativeFlow,
    selectedCreatives,
    creativeOverrides,
    selectedPixelId,
  ])

  const handleRetry = useCallback(() => {
    setErrorMessage("")
    if (step === "error" && structures.length === 0) {
      if (isCreativeFlow) {
        void handleGenerateCreativeAdsets()
      } else if (campaignData.adsetStrategy === "Product Groups") {
        void handleGenerateAdsets()
      } else {
        void handleLaunch()
      }
    } else {
      void handleLaunch()
    }
  }, [
    handleLaunch,
    handleGenerateAdsets,
    handleGenerateCreativeAdsets,
    step,
    campaignData.adsetStrategy,
    structures.length,
    isCreativeFlow,
  ])

  const handleEnterReview = useCallback(async () => {
    setStep("review")
    if (needsPixel) {
      setPixelsLoading(true)
      try {
        const { data } = await fetchPixels({
          optimizationGoal: campaignData.optimizationGoal,
          campaignType: campaignData.campaignType,
        })
        if (data) {
          setPixelsData(data)
          setSelectedPixelId(data.recommendedPixelId)
        }
      } catch {
        // Non-blocking: pixel fetch failure doesn't prevent launch
      } finally {
        setPixelsLoading(false)
      }
    }
  }, [
    needsPixel,
    fetchPixels,
    campaignData.optimizationGoal,
    campaignData.campaignType,
  ])

  const handleReset = useCallback(() => {
    setStep("setup")
    setCampaignData(DEFAULT_CAMPAIGN_DATA)
    setAdsets([])
    setStructures([])
    setSelectedCreatives([])
    setCreativeOverrides({})
    setErrorMessage("")
    setCreatedCampaignId("")
    setLaunchResult(null)
    setSelectedPixelId(null)
    setPixelsData(null)
  }, [])

  // Map non-wizard steps to their wizard equivalents for progress display
  const displayStep = (() => {
    if (step === "custom-builder") return "structures"
    if (step === "creativeSelection") return "structures"
    if (step === "creativeDetails") return "structures"
    return step
  })()
  const stepIndex = STEPS.findIndex(s => s.key === displayStep)
  const isWizardStep = stepIndex >= 0

  const generatingTitle = isCreativeFlow
    ? "Analyzing Creative"
    : "Analyzing Product Data"
  const generatingDescription = isCreativeFlow
    ? "AI is generating optimal adset groupings based on your selected creative..."
    : "AI is generating optimal adset groupings based on your brand's product data..."

  return (
    <MetaAdAccountGate
      title="Select Ad Account for Campaign Blaster"
      selectedAdAccountId={selectedAdAccountId}
      onSelect={setSelectedAdAccountId}
    >
      <div className="p-6 container mx-auto">
        <h1 className="text-2xl font-bold text-basic-white mb-6">
          Campaign Blaster
        </h1>

        {isWizardStep && (
          <div
            className="grid mb-8"
            style={{
              gridTemplateColumns: `repeat(${STEPS.length * 2 - 1}, auto)`,
            }}
          >
            {STEPS.map((s, i) => {
              const isActive = i === stepIndex
              const isCompleted = i < stepIndex
              let stepStyle = "bg-system-gray-900/50 text-basic-light/40"
              if (isActive) stepStyle = "bg-primary text-primary-content"
              else if (isCompleted) stepStyle = "bg-primary/20 text-primary"
              return (
                <div key={s.key} className="flex items-center">
                  <div className="flex items-center gap-2 shrink-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${stepStyle}`}
                    >
                      {isCompleted ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                          stroke="currentColor"
                          className="w-4 h-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m4.5 12.75 6 6 9-13.5"
                          />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span
                      className={`text-sm hidden sm:block ${
                        isActive
                          ? "text-basic-white font-medium"
                          : "text-basic-light/40"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`h-px flex-1 mx-3 ${
                        isCompleted ? "bg-primary/40" : "bg-system-gray-700/50"
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className="bg-system-blue-800 rounded-lg p-6">
          {step === "setup" && (
            <CampaignSetup
              data={campaignData}
              onChange={data => {
                setCampaignData(data)
                if (errorFieldHints.length > 0) setErrorFieldHints([])
              }}
              onNext={handleSetupNext}
              errorFields={errorFieldHints}
            />
          )}

          {step === "creativeSelection" && (
            <CreativeSelectionStep
              selectedCreatives={selectedCreatives}
              onSelectionChange={setSelectedCreatives}
              onNext={() => {
                const hasOrganic = selectedCreatives.some(
                  c => c.type === "organic",
                )
                const goalNeedsOverrides =
                  campaignData.optimizationGoal !== "Profile Visits" &&
                  campaignData.optimizationGoal !== "Post Engagements"
                if (hasOrganic && goalNeedsOverrides) {
                  setStep("creativeDetails")
                } else {
                  void handleGenerateCreativeAdsets()
                }
              }}
              onBack={() => setStep("setup")}
            />
          )}

          {step === "creativeDetails" && (
            <CreativeDetailsStep
              overrides={creativeOverrides}
              onChange={setCreativeOverrides}
              optimizationGoal={campaignData.optimizationGoal}
              onNext={() => void handleGenerateCreativeAdsets()}
              onBack={() => setStep("creativeSelection")}
            />
          )}

          {step === "generating" && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <span className="loading loading-spinner loading-lg text-primary" />
              <h3 className="text-lg font-semibold text-basic-white">
                {generatingTitle}
              </h3>
              <p className="text-sm text-basic-light/60">
                {generatingDescription}
              </p>
            </div>
          )}

          {step === "structures" && (
            <StructureSelection
              structures={structures}
              onSelect={handleStructureSelect}
              onBuildOwn={() => setStep("custom-builder")}
              onBack={() =>
                setStep(isCreativeFlow ? "creativeSelection" : "setup")
              }
              selectedCreatives={isCreativeFlow ? selectedCreatives : undefined}
            />
          )}

          {step === "custom-builder" &&
            (isCreativeFlow ? (
              <CustomCreativeBuilder
                selectedCreatives={selectedCreatives}
                onComplete={customAdsets => {
                  setAdsets(customAdsets)
                  setStep("adsets")
                }}
                onBack={() => setStep("structures")}
              />
            ) : (
              <CustomStructureBuilder
                onComplete={customAdsets => {
                  setAdsets(customAdsets)
                  setStep("adsets")
                }}
                onBack={() => setStep("structures")}
              />
            ))}

          {step === "adsets" && (
            <AdsetReview
              campaignTitle={campaignTitle}
              adsets={adsets}
              campaignData={campaignData}
              onAdsetsChange={setAdsets}
              onNext={() => void handleEnterReview()}
              onBack={() =>
                setStep(
                  campaignData.adsetStrategy === "Product Groups" ||
                    isCreativeFlow
                    ? "structures"
                    : "setup",
                )
              }
            />
          )}

          {step === "review" && (
            <FinalReview
              campaignTitle={campaignTitle}
              campaignData={campaignData}
              adsets={adsets}
              onLaunch={handleLaunch}
              onBack={() => setStep("adsets")}
              pixels={pixelsData?.pixels}
              selectedPixelId={selectedPixelId}
              onPixelChange={setSelectedPixelId}
              recommendedPixelId={pixelsData?.recommendedPixelId}
              recommendedReason={pixelsData?.recommendedReason}
              pixelsLoading={pixelsLoading}
            />
          )}

          {step === "processing" && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <span className="loading loading-spinner loading-lg text-primary" />
              <h3 className="text-lg font-semibold text-basic-white">
                Creating Campaign on Meta
              </h3>
              <p className="text-sm text-basic-light/60">
                {isCreativeFlow
                  ? "Creating your campaign, adsets, and ads as PAUSED..."
                  : "Creating your campaign and adsets as PAUSED..."}
              </p>
            </div>
          )}

          {step === "success" && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <CheckCircleIcon className="w-16 h-16 text-success" />
              <h3 className="text-lg font-semibold text-basic-white">
                Campaign Created Successfully!
              </h3>
              <p className="text-sm text-basic-light/60">
                {launchResult?.adResults
                  ? "Your campaign, adsets, and ads have been created as PAUSED. Activate in Meta Ads Manager when ready."
                  : "Your campaign and adsets have been created as PAUSED. Add ads via Ad Forge, then activate in Meta Ads Manager."}
              </p>
              {createdCampaignId && (
                <p className="text-xs text-basic-light/40 font-mono">
                  Campaign ID: {createdCampaignId}
                </p>
              )}

              {/* Ad creation results summary */}
              {launchResult?.adResults && (
                <div className="w-full max-w-md space-y-2">
                  <div className="flex items-center justify-center gap-4 text-sm">
                    <span className="text-success">
                      {launchResult.adResults.successful} ads created
                    </span>
                    {launchResult.adResults.failed > 0 && (
                      <span className="text-error">
                        {launchResult.adResults.failed} failed
                      </span>
                    )}
                  </div>

                  {/* Show failed ads details */}
                  {launchResult.adResults.failed > 0 && (
                    <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-left">
                      <p className="text-xs font-medium text-error mb-1.5">
                        Failed to create ads for:
                      </p>
                      <ul className="space-y-1">
                        {launchResult.adResults.details
                          .filter(d => !d.success)
                          .map((d, i) => (
                            <li key={i} className="text-xs text-basic-light/60">
                              <span className="font-medium text-basic-light/80">
                                {d.creativeTitle || d.creativeId}
                              </span>
                              {d.error && (
                                <span className="text-basic-light/40">
                                  {" "}
                                  &mdash; {d.error}
                                </span>
                              )}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <button className="btn btn-primary mt-4" onClick={handleReset}>
                Done
              </button>
            </div>
          )}

          {step === "error" && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <ExclamationTriangleIcon className="w-16 h-16 text-error" />
              <h3 className="text-lg font-semibold text-basic-white">
                Something Went Wrong
              </h3>
              <p className="text-sm text-basic-light/60 max-w-lg text-center">
                {errorMessage}
              </p>
              {errorFieldHints.length > 0 && (
                <div className="bg-error/10 border border-error/30 rounded-lg px-4 py-3 max-w-lg w-full">
                  <p className="text-xs text-error font-medium mb-1">
                    Review these settings:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {errorFieldHints.map(field => (
                      <span
                        key={field}
                        className="px-2 py-0.5 bg-error/20 text-error text-xs rounded-full"
                      >
                        {FIELD_DISPLAY_NAMES[field] ?? field}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <button className="btn btn-ghost" onClick={handleReset}>
                  Cancel
                </button>
                {errorFieldHints.length > 0 && (
                  <button
                    className="btn btn-outline btn-warning"
                    onClick={() => {
                      setStep("setup")
                    }}
                  >
                    Edit Settings
                  </button>
                )}
                <button className="btn btn-primary" onClick={handleRetry}>
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MetaAdAccountGate>
  )
}
